import React from "react";
import moment from "moment";

const AgreementContent = React.forwardRef((props, ref) => {
  const { formValues } = props;

  if (!formValues) return null;

  const {
    personal_details,
    travelers,
    original_flight_details,
    type_of_claim,
  } = formValues;

  // Map RHF data to your agreement variables
  const clientName = `${personal_details?.first_name || ""} ${personal_details?.last_name || ""}`.trim();
  const clientAddress = `${personal_details?.address?.label || personal_details?.address  || ""}, ${personal_details?.city || ""}, ${personal_details?.state || ""} ${personal_details?.postal_code || ""}`.trim();
  const passportNumber = personal_details?.passport_number;
  const clientTicketNumber = personal_details?.tickets;
  const familyMembers = travelers?.filter(t => t.first_name || t.last_name) || [];
  const flightDetails = original_flight_details || [];
  const date = new Date().toISOString(); // Uses current date for "Concluded on..."

  function getClaimType(type) {
    switch (type) {
      case "overbooking":
      case "denied_boarding":
        return "Denied boarding";
      case "lost_baggage":
        return "Lost luggage";
      case "damage_baggage":
        return "Damaged luggage";
      case "travel_expenses":
        return "Travel expenses";
      case "delayed_baggage":
        return "Delayed luggage";
      case "delayed":
        return "Delayed";
      case "cancelled":
        return "Cancelled";
      case "lost/damaged":
        return "Lost/Damaged";
      case "lost_wages":
        return "Lost Wages";
      case "other":
      case "others":
        return "Others";
      default:
        return type || "Flight Claim";
    }
  }

  const formatDate = (dateOfBirth) => {
    if (!dateOfBirth) return "";
    const formats = [
      "DD-MM-YYYY", "MM-DD-YYYY", "YYYY-MM-DD",
      "DD/MM/YYYY", "MM/DD/YYYY", "YYYY/MM/DD",
      "DD.MM.YYYY", "MM.DD.YYYY",
    ];
    const parsed = moment(dateOfBirth, formats, true);
    if (!parsed.isValid()) {
      return dateOfBirth; // Fallback to raw string if moment fails
    }
    return parsed.format("MM-DD-YYYY");
  };

  const renderClaimText = (flight) => {
    return (
      <>
        The Client, acting on its own behalf [and on behalf of the persons it is representing]
        declares that it has claims against {flight.airline || "the airline"}{" "}
        for compensation for:
        <ol>
          <li>
            {getClaimType(type_of_claim)} flight ({flight.flight_number} on {formatDate(flight.date_of_travel)})
          </li>
        </ol>
        increased by potential claims for damages suffered and interest for late payment (the <strong>“Claims”</strong>).
      </>
    );
  };

  const FlightDetailsList = () => {
    if (flightDetails.length > 1) {
      return (
        <ol style={{ paddingLeft: "20px" }}>
          <li>
            <ul style={{ listStyleType: "lower-roman", paddingLeft: "20px" }}>
              {flightDetails.map((flight, index) => (
                <React.Fragment key={index}>
                  <li>{renderClaimText(flight)}</li>
                  {index < flightDetails.length - 1 && <br />}
                </React.Fragment>
              ))}
            </ul>
          </li>
        </ol>
      );
    } else if (flightDetails.length === 1 && flightDetails[0].flight_number) {
      const flight = flightDetails[0];
      return <p>1. {renderClaimText(flight)}</p>;
    } else {
      return <p>No flight details provided.</p>;
    }
  };

  return (
    <div
      ref={ref}
      style={{ fontFamily: "Roboto, Helvetica, Arial, sans-serif", display: "none" }}
    >
      <div
        style={{
          position: "relative",
          padding: "16px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "1px 1px 5px rgba(0, 0, 0, 0.1)",
          breakInside: "avoid",
        }}
      >
        <h1 style={{ textAlign: "center", marginBottom: "30px", fontSize: "18px", textTransform: "uppercase", fontWeight: "600" }}>
          Assignment Agreement
        </h1>

        <h2 style={{ marginBottom: "16px" }}>
          Concluded on {formatDate(date)} by and between:
        </h2>

        <p>
          {clientName}, residing at {clientAddress}, passport number{" "}
          {passportNumber}, booking number {clientTicketNumber},
        </p>
        <p>
          hereinafter referred to as the <strong>“Client”</strong>,
        </p>

        {familyMembers.length > 0 && (
          <>
            <p>[acting at the same time on behalf of:</p>
            <ol>
              {familyMembers.map((member, index) => (
                <li key={index}>
                  <p>
                    {member.first_name} {member.last_name}, born{" "}
                    {formatDate(member.date_of_birth)}, passport number{" "}
                    {member.passport_number}, ticket number{" "}
                    {member.tickets}
                  </p>
                </li>
              ))}
            </ol>
          </>
        )}

        <p>
          Fly And Claim Inc, 11 Main Street, Monsey NY 10952. USA, EIN: 41-3703352
          (hereinafter <strong>“F&C USA”</strong>),
        </p>
        <p>and</p>
        <p>
          Fly and Claim Travel Services Sp. z o.o., ul. Mogilska 43, 31-545 Kraków, Poland, KRS:
          0001247476, NIP: 6751829771, REGON: 54500165800000 ( hereinafter <strong>“F&C Poland”</strong>),
        </p>
        <p>
          hereinafter together referred to as the <strong>“Provider”</strong>.
        </p>

        <hr style={{ margin: "20px 0" }} />

        <section style={{ marginBottom: "24px" }}>
          <h2 style={{ textAlign: "center", marginBottom: "16px" }}>
            <strong>§1</strong>
          </h2>
          <FlightDetailsList />
          
          <p>
            2. The Client declares that he remains fully entitled to the Claims and that it has not
            been assigned to any third parties nor have any third parties been engaged in trying
            to assert the Claims.
          </p>
          <p>
            3. The Client hereby assigns the Claims and all rights arising therein to F&C USA and
            F&C Poland, allowing them to assert the Claims in their own name (either jointly or
            independently) and to conduct all legal action, negotiations and make any
            settlements with the airline at its discretion. F&C USA and F&C Poland are further
            entitled to collect any and all compensation received from the airline (either jointly or independently - at their discretion) and are later obliged to transfer it to the Client
            after offsetting the agreed fee.
          </p>
          <p>
            4. F&C USA and F&C Poland act with due diligence and strive to acquire compensation
            on behalf of the Client. F&C USA and F&C Poland do not, however, guarantee that
            their services will be successful in acquiring the compensation and may therefore not
            be held liable solely based on the fact that the services did not result in receipt of
            compensation. The Client is aware that it may take many months (even years)
            before any compensation is potentially acquired.
          </p>
          <p>
            5. Additional clauses related to this Assignment Agreement, in particular the fee
            charged by F&C USA and F&C Poland, obligations of the Parties and limitations of
            liability, are subject to the Fly and Claim’s Terms and Conditions which constitute an
            integral part of this Assignment Agreement.
          </p>
          <p>
            6. F&C USA and F&C Poland may pursue the claims on any legal basis and in any
            jurisdiction/country that may be applicable.
          </p>
        </section>

        <hr style={{ margin: "20px 0" }} />

        <section style={{ marginBottom: "24px" }}>
          <h2 style={{ textAlign: "center", marginBottom: "16px" }}>
            <strong>§2</strong>
          </h2>
          <p>
            1. This Assignment Agreement is subject to Polish law and the jurisdiction of Polish
            courts.
          </p>
          <p>2. The Assignment Agreement was concluded in two copies.</p>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ width: "250px", height: "70px" }} className="signatureContainer"></div>
              <p style={{ marginBottom: "10px" }}>___________________________________</p>
              <p><strong>F&C Poland</strong></p>
            </div>
            
            <div style={{ textAlign: "center" }}>
              <div style={{ width: "250px", height: "70px" }}></div>
              <p style={{ marginBottom: "10px" }}>___________________________________</p>
              <p><strong>Client signature</strong></p>
            </div>

            <div style={{ textAlign: "center" }}>
              <div style={{ width: "250px", height: "70px" }} className="signatureContainer"></div>
              <p style={{ marginBottom: "10px" }}>___________________________________</p>
              <p><strong>F&C USA</strong></p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
});

export default AgreementContent;